import React from "react";
import { Link } from "react-router-dom";
import { LOGO } from "@/lib/constants";
import { Mail, Phone, MapPin } from "lucide-react";
import { TrustBadge } from "@/components/TrustBadge";
import { useLang } from "@/context/LanguageContext";

const GUIDES = [
  { to: "/visa-australie", label: "Visa Australie" },
  { to: "/visa-canada", label: "Visa Canada" },
  { to: "/pvt-canada", label: "PVT Canada" },
  { to: "/travailler-en-australie", label: "Travailler en Australie" },
  { to: "/immigration-canada", label: "Immigration Canada" },
  { to: "/guide-australie", label: "Guide Australie" },
];

export const Footer = () => {
  const { t } = useLang();
  return (
  <footer data-testid="main-footer" className="bg-navy text-white">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
      <div className="sm:col-span-2 lg:col-span-1">
        <div className="bg-white rounded-full h-20 w-20 flex items-center justify-center p-1">
          <img src={LOGO} alt="Work & Travel Expert" className="h-full w-full object-contain rounded-full" />
        </div>
        <p className="mt-5 text-sm text-white/70 leading-relaxed">{t("footer.tagline")}</p>
        <TrustBadge className="mt-5" />
      </div>

      <div>
        <h4 className="font-heading text-lg mb-4 text-gold">{t("footer.navigation")}</h4>
        <ul className="space-y-2 text-sm text-white/80">
          <li><Link to="/" className="hover:text-gold transition-colors">{t("nav.home")}</Link></li>
          <li><Link to="/a-propos" className="hover:text-gold transition-colors">{t("nav.about")}</Link></li>
          <li><Link to="/nos-packs" className="hover:text-gold transition-colors">{t("nav.packs")}</Link></li>
          <li><Link to="/blog" className="hover:text-gold transition-colors">Blog</Link></li>
          <li><Link to="/avis" className="hover:text-gold transition-colors">Avis clients</Link></li>
          <li><Link to="/entreprise-verifiee" data-testid="footer-verified-link" className="hover:text-gold transition-colors">Entreprise vérifiée</Link></li>
          <li><Link to="/preinscription-canada-2027" data-testid="footer-prereg-link" className="hover:text-gold transition-colors">Préinscription Canada 2027</Link></li>
          <li><Link to="/wte-live" data-testid="footer-live-link" className="hover:text-gold transition-colors">WTE LIVE — actualités</Link></li>
          <li><Link to="/faq" className="hover:text-gold transition-colors">{t("nav.faq")}</Link></li>
          <li><Link to="/contact" className="hover:text-gold transition-colors">{t("nav.contact")}</Link></li>
        </ul>
      </div>

      <div>
        <h4 className="font-heading text-lg mb-4 text-gold">Guides &amp; Ressources</h4>
        <ul className="space-y-2 text-sm text-white/80">
          {GUIDES.map((g) => (
            <li key={g.to}>
              <Link to={g.to} data-testid={`footer-guide-${g.to.slice(1)}`} className="hover:text-gold transition-colors">{g.label}</Link>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="font-heading text-lg mb-4 text-gold">{t("footer.packs")}</h4>
        <ul className="space-y-2 text-sm text-white/80">
          <li><Link to="/nos-packs" className="hover:text-gold transition-colors">Pack Visa Canada</Link></li>
          <li><Link to="/nos-packs" className="hover:text-gold transition-colors">Pack Visa Australie</Link></li>
          <li><Link to="/nos-packs" className="hover:text-gold transition-colors">Pack Préparation au départ</Link></li>
          <li><Link to="/nos-packs" className="hover:text-gold transition-colors">Pack Installation à l'arrivée</Link></li>
          <li><Link to="/nos-packs" className="hover:text-gold transition-colors">Pack Premium</Link></li>
        </ul>
      </div>

      <div>
        <h4 className="font-heading text-lg mb-4 text-gold">{t("footer.contact")}</h4>
        <ul className="space-y-3 text-sm text-white/80">
          <li className="flex items-center gap-3 min-w-0"><Mail size={16} className="text-gold shrink-0" /> <a href="mailto:worktravelexpert@hotmail.com" className="hover:text-gold transition-colors truncate">worktravelexpert@hotmail.com</a></li>
          <li className="flex items-center gap-3"><Phone size={16} className="text-gold" /> <a href="https://wa.me/33749857587" target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors">WhatsApp : +33 7 49 85 75 87</a></li>
          <li className="flex items-start gap-3"><MapPin size={16} className="text-gold mt-0.5" /> {t("footer.address")}</li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 pb-24 sm:pb-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50">
        <p>© {new Date().getFullYear()} Work &amp; Travel Expert LLC. {t("footer.rights")}</p>
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
          <Link to="/mentions-legales" className="hover:text-gold transition-colors">{t("footer.legal")}</Link>
          <Link to="/confidentialite" className="hover:text-gold transition-colors">{t("footer.privacy")}</Link>
          <Link to="/cgv" className="hover:text-gold transition-colors">{t("footer.cgv")}</Link>
        </div>
      </div>
    </div>
  </footer>
  );
};
